<template>
  <v-dialog 
    v-model="dialog" 
    max-width="1200px"
    scrollable
    persistent>
    <v-card>
      <dialog-confirm ref="reprocess-confirm" :confirm-text="$t('reprocess_import')">
        <template #title>{{ $t('reprocess_import') }}</template>
        {{ $t('reprocess_file_upload_confirmation', { filename: importData ? importData.original_filename : '' }) }}
      </dialog-confirm>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-file-document-outline</v-icon>
        <div>
          <div class="text-h6">{{ $t('file_upload_details') }}</div>
          <div class="text-subtitle-2 text--secondary" v-if="importData">
            {{ importData.original_filename }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-chip 
          v-if="importData"
          :color="getStatusColor(importData.status)" 
          :text-color="getStatusTextColor(importData.status)"
          label>
          <v-icon left small>{{ getStatusIcon(importData.status) }}</v-icon>
          {{ importData.status_text }}
        </v-chip>
        <v-btn icon @click="close" class="ml-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-container fluid v-if="importData">
          <!-- Overview Section -->
          <v-row>
            <v-col cols="12">
              <v-card flat class="mb-4">
                <v-card-title class="text-h6 pb-2">
                  <v-icon class="mr-2">mdi-chart-line</v-icon>
                  {{ $t('overview') }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2" :class="getMetricColor('total')">
                          {{ importData.total_routes_in_file }}
                        </div>
                        <div class="text-caption text--secondary">{{ $t('total_routes') }}</div>
                      </div>
                    </v-col>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2 success--text">
                          {{ importData.route_plans_created + importData.route_plans_updated }}
                        </div>
                        <div class="text-caption text--secondary">{{ $t('processed_successfully') }}</div>
                      </div>
                    </v-col>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2" :class="importData.route_plans_failed > 0 ? 'error--text' : 'text--secondary'">
                          {{ importData.route_plans_failed }}
                        </div>
                        <div class="text-caption text--secondary">{{ $t('failed') }}</div>
                      </div>
                    </v-col>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2 info--text">
                          {{ importData.route_plans_created }}
                        </div>
                        <div class="text-caption text--secondary">{{ $t('created') }}</div>
                      </div>
                    </v-col>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2 warning--text">
                          {{ importData.route_plans_updated }}
                        </div>
                        <div class="text-caption text--secondary">{{ $t('updated') }}</div>
                      </div>
                    </v-col>
                    <v-col md="4" cols="6">
                      <div class="text-center">
                        <div class="text-h2 success--text">
                          {{ importData.success_rate }}%
                        </div>
                        <div class="text-caption text--secondary">{{ $t('success_rate') }}</div>
                      </div>
                    </v-col>
                  </v-row>
                  
                  <!-- Progress Bar -->
                  <v-progress-linear
                    v-if="importData.total_routes_in_file > 0"
                    :value="getProgressPercentage()"
                    :color="getProgressColor()"
                    height="12"
                    rounded
                    class="mt-4">
                    <template #default="{ value }">
                      <strong>{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- File Information -->
          <v-row>
            <v-col cols="12">
              <v-card flat>
                <v-card-title class="text-h6 pb-2">
                  <v-icon class="mr-2">mdi-information</v-icon>
                  {{ $t('file_information') }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col md="4" cols="6">
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('original_filename') }}</v-list-item-title>
                            <v-list-item-subtitle>{{ importData.original_filename }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('file_size') }}</v-list-item-title>
                            <v-list-item-subtitle>{{ importData.formatted_file_size }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                    <v-col md="4" cols="6">
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('upload_method') }}</v-list-item-title>
                            <v-list-item-subtitle>{{ importData.upload_method_text }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="importData.uploader">
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('uploaded_by') }}</v-list-item-title>
                            <v-list-item-subtitle>{{ importData.uploader.name }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                    <v-col md="4" cols="6">
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('uploaded_at') }}</v-list-item-title>
                            <v-list-item-subtitle>
                              <multi-time-display :time="importData.uploaded_at" />
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="importData.processed_at">
                          <v-list-item-content>
                            <v-list-item-title>{{ $t('processed_at') }}</v-list-item-title>
                            <v-list-item-subtitle>
                              <multi-time-display :time="importData.processed_at" />
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Route Plans Details -->
          <v-row v-if="importData.route_plans && importData.route_plans.length > 0">
            <v-col cols="12">
              <v-card flat>
                <v-card-title class="text-h6 pb-2">
                  <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                  {{ $t('route_plans_processed') }}
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="routeSearch"
                    :label="$t('search_routes')"
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    dense
                    outlined
                    clearable
                    style="max-width: 300px;">
                  </v-text-field>
                </v-card-title>
                <v-card-text class="pa-0">
                  <v-data-table
                    :headers="routeHeaders"
                    :items="filteredRoutes"
                    :items-per-page="10"
                    class="elevation-0"
                    :loading="loading"
                    sort-by="created"
                    sort-desc>
                    
                    <template #item.route_number="{ item }">
                      <router-link 
                        v-if="item.route_plan_id"
                        :to="`/route-plan/${item.route_plan_id}`">
                        {{ item.route_number || 'N/A' }}
                      </router-link>
                      <span v-else class="text--disabled">{{ item.route_number || 'N/A' }}</span>
                    </template>

                    <template #item.action="{ item }">
                      <v-chip 
                        :color="getActionColor(item.action)" 
                        small
                        label>
                        <v-icon left small>{{ getActionIcon(item.action) }}</v-icon>
                        {{ item.action_text }}
                      </v-chip>
                    </template>

                    <template #item.error_message="{ item }">
                      <div v-if="item.error_message" class="error--text text-caption">
                        {{ item.error_message }}
                      </div>
                      <span v-else class="text--disabled">—</span>
                    </template>

                    <template #item.row_number="{ item }">
                      <span v-if="item.row_number" class="text-caption">
                        {{ $t('row') }} {{ item.row_number }}
                      </span>
                      <span v-else class="text--disabled">—</span>
                    </template>

                    <template #item.created="{ item }">
                      <multi-time-display :time="item.created" format="short" />
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Processing Logs -->
          <v-row v-if="importData.processing_logs && importData.processing_logs.length > 0">
            <v-col cols="12">
              <v-card flat>
                <v-card-title class="text-h6 pb-2">
                  <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                  {{ $t('processing_logs') }}
                </v-card-title>
                <v-card-text>
                  <div class="logs-container" style="max-height: 400px; overflow-y: auto;">
                    <v-timeline dense>
                      <v-timeline-item
                        v-for="(log, index) in importData.processing_logs"
                        :key="index"
                        :color="getLogColor(log.message)"
                        small>
                        <div class="text-caption text--secondary">
                          {{ formatLogTimestamp(log.timestamp) }}
                        </div>
                        <div class="text-body-2">{{ log.message }}</div>
                        <div v-if="log.context && Object.keys(log.context).length > 0" class="text-caption mt-1">
                          <code class="grey lighten-4 pa-1">{{ JSON.stringify(log.context, null, 2) }}</code>
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <div class="d-flex w-100 align-center">
          <div v-if="canReprocess" class="mr-auto">
            <v-btn
              color="warning"
              @click="reprocessFileUpload"
              :loading="reprocessing">
              <v-icon left>mdi-refresh</v-icon>
              {{ $t('reprocess_import') }}
            </v-btn>
          </div>
          <v-btn
            outlined
            @click="close">
            {{ $t('close') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import DialogConfirm from '@/components/shared/DialogConfirm'

export default {
  name: 'RoutePlanFileUploadDetailModal',
  components: {
    MultiTimeDisplay,
    DialogConfirm
  },
  data () {
    return {
      dialog: false,
      loading: false,
      reprocessing: false,
      importData: null,
      routeSearch: '',
      routeHeaders: [
        {
          text: this.$t('route_number'),
          value: 'route_number',
          sortable: true
        },
        {
          text: this.$t('action'),
          value: 'action',
          sortable: true
        },
        {
          text: this.$t('error_message'),
          value: 'error_message',
          sortable: false
        },
        {
          text: this.$t('csv_row'),
          value: 'row_number',
          sortable: true
        },
        {
          text: this.$t('processed_at'),
          value: 'created',
          sortable: true
        }
      ]
    }
  },
  computed: {
    canReprocess () {
      return this.importData && ['failed', 'partially_failed'].includes(this.importData.status)
    },

    hasErrors () {
      return this.importData && this.importData.route_plans_failed > 0
    },

    filteredRoutes () {
      if (!this.importData || !this.importData.route_plans) return []
      
      if (!this.routeSearch) return this.importData.route_plans
      
      const search = this.routeSearch.toLowerCase()
      return this.importData.route_plans.filter(route => {
        return (
          (route.route_number && route.route_number.toLowerCase().includes(search)) ||
          (route.error_message && route.error_message.toLowerCase().includes(search)) ||
          (route.action && route.action.toLowerCase().includes(search))
        )
      })
    }
  },
  methods: {
    async show (importId) {
      this.dialog = true
      this.loading = true
      this.importData = null

      try {
        const response = await this.$httpAdapter.get(`route-plan-file-upload/${importId}`)
        this.importData = response.data
      } catch (error) {
        console.error('Error loading import details:', error)
        const errorMessage = error?.response?.data?.message || error?.message || this.$t('failed_to_load_file_upload_details')
        this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', errorMessage)
      } finally {
        this.loading = false
      }
    },

    close () {
      this.dialog = false
      this.importData = null
      this.routeSearch = ''
    },

    async reprocessFileUpload () {
      if (!this.importData) return

      const confirmed = await this.$refs['reprocess-confirm'].confirm()
      if (!confirmed) return

      this.reprocessing = true
      try {
        const uploadId = this.importData.id
        const response = await this.$httpAdapter.post(`route-plan-file-upload/${uploadId}/reprocess`)

        if (response.data.success) {
          this.$store.dispatch('globalMessagingModule/showSuccessFlashBanner', this.$t('file_upload_reprocessing_initiated'))
          this.$emit('reprocess', uploadId)
          this.close()
        } else {
          this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', response.data.message || this.$t('reprocessing_failed'))
        }
      } catch (error) {
        console.error('Error reprocessing import:', error)
        const errorMessage = error?.response?.data?.message || error?.message || this.$t('reprocessing_failed')
        this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', errorMessage)
      } finally {
        this.reprocessing = false
      }
    },


    getProgressPercentage () {
      if (!this.importData || this.importData.total_routes_in_file === 0) return 0
      return Math.round((this.importData.processed_count / this.importData.total_routes_in_file) * 100)
    },

    getProgressColor () {
      if (!this.importData) return 'grey'
      if (this.importData.status === 'completed') return 'success'
      if (this.importData.status === 'failed') return 'error'
      if (this.importData.status === 'partially_failed') return 'warning'
      if (this.importData.status === 'processing') return 'primary'
      return 'grey'
    },

    getStatusColor (status) {
      switch (status) {
        case 'completed': return 'success'
        case 'processing': return 'primary'
        case 'pending': return 'grey'
        case 'failed': return 'error'
        case 'partially_failed': return 'warning'
        default: return 'grey'
      }
    },

    getStatusTextColor (status) {
      return ['pending', 'grey'].includes(status) ? 'black' : 'white'
    },

    getStatusIcon (status) {
      switch (status) {
        case 'completed': return 'mdi-check-circle'
        case 'processing': return 'mdi-loading'
        case 'pending': return 'mdi-clock-outline'
        case 'failed': return 'mdi-alert-circle'
        case 'partially_failed': return 'mdi-alert'
        default: return 'mdi-help-circle'
      }
    },

    getActionColor (action) {
      switch (action) {
        case 'created': return 'success'
        case 'updated': return 'warning'
        case 'failed': return 'error'
        default: return 'grey'
      }
    },

    getActionIcon (action) {
      switch (action) {
        case 'created': return 'mdi-plus-circle'
        case 'updated': return 'mdi-pencil-circle'
        case 'failed': return 'mdi-alert-circle'
        default: return 'mdi-help-circle'
      }
    },

    getMetricColor (type) {
      if (type === 'total') return 'primary--text'
      return ''
    },

    getLogColor (message) {
      if (!message) return 'grey'
      const lowerMessage = message.toLowerCase()
      if (lowerMessage.includes('error') || lowerMessage.includes('failed')) return 'error'
      if (lowerMessage.includes('warning')) return 'warning'
      if (lowerMessage.includes('success') || lowerMessage.includes('completed')) return 'success'
      return 'primary'
    },

    formatLogTimestamp (timestamp) {
      return this.$moment(timestamp).format('HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.logs-container {
  background-color: #fafafa;
  border-radius: 4px;
  padding: 16px;
}
</style>